import React, { useMemo, useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { CCard, CCardGroup } from "@coreui/react";
import { toast } from "react-toastify";
import { useTranslationContext } from "@/context/TranslationContext";
import { TranslationService } from "@/api";
import SmsTemplateForm from "../smsTemplateForm";
import { smsTemplate } from "@/constants/translation/smsTemplate.translation";
import { breadcrumb_key } from "@/constants/translation/common.translation";
import SmsTemplateValidationClass from "@/helpers/validations/smsTemplate";
import { messages } from "@/constants/translation/common.translation";
import { SmsTemplateService } from "@/api/smsTemplateService";     
import { masters } from "@/libs/routes";
import constants from "@/libs/constant";
        
        
/**
 * Component for editing an SMS template.
 * React component for editing an existing SMS template.
 * 
 * This component fetches an SMS template by ID, allows modifications, and updates it upon form submission.
 * @returns {JSX.Element} The rendered EditSmsTemplate component.
 */
const EditSmsTemplate = () => {
  const { id } = useParams(); // Extract "id" parameter from URL
  const navigate = useNavigate();
  const t = useTranslationContext();
  
  
  
  const [isLoading, setIsLoading] = useState(false);
  const [isDataLoading, setIsDataLoading] = useState(false);
  const [languages, setLanguages] = useState([]);
  const [smsTemplateData, setSmsTemplateData] = useState(null);

  useEffect(() => {
    // Fetch languages on component mount
    const fetchLanguages = async () => {
      
      try {
        
        const languagesResponse = await TranslationService.getLanguages();
        
        
        const languagesData = languagesResponse.data || languagesResponse;
        let languagesList = [];
        
        if (Array.isArray(languagesData)) {
          languagesList = languagesData;
        } else if (languagesData && typeof languagesData === constants.dataTypes.object) {
          languagesList = Object.values(languagesData).map(lang => ({
            id: lang.id,
            name: lang.name,
            code: lang.code,
            isDefault: lang.isDefault,
            displayName: lang.name
          }));
          
          // Sort languages: English first, then others alphabetically
          languagesList.sort((a, b) => {
            if (a.name.toLowerCase() === constants.defaultLanguage) return -1;
            if (b.name.toLowerCase() === constants.defaultLanguage) return 1;
            return a.name.localeCompare(b.name);
          });
        }
        
        
        setLanguages(languagesList);
      } catch (error) {
        console.error("Fetch languages error:", error);
        toast.error(t(messages.toast.translation.languagesFetchFailed));
      }
    };
    
    fetchLanguages();
  }, []);

  useEffect(() => {
    // Fetch SMS template data when component mounts and languages are loaded
    const fetchSmsTemplate = async () => {
      
      if (!id || languages.length === 0) {
        
        return;
      }
      
      setIsDataLoading(true);
      try {
        
    
        const response = await SmsTemplateService.get(id);
        
        setSmsTemplateData(response.data);
      } catch (error) {
        console.error("Fetch SMS template error:", error);
        toast.error(t(messages.toast.smsTemplate.dataFetchFailed));
      } finally {
        setIsDataLoading(false);
      }
    };
    
    fetchSmsTemplate();
  }, [id, languages, t]);

  const handleUpdateSmsTemplate = async (values) => {
    
    
    
    setIsLoading(true);
    try {
      
      
      
      // Transform data structure to match API expectations
      const apiData = {
        uuid: id, // Use uuid as main identifier
        slug: values.slug,
        parameter: values.parameter,
        templates: values.sms_templates.map(template => ({
          uuid: template.uuid || null,
          language_id: template.language_id, // Use language_id (snake_case) for API
          title: template.title,
          subject: "", // SMS templates don't have subject
          content: template.content
        }))
      };
      
      
      
      const response = await SmsTemplateService.update(apiData);
      
      toast.success(response.message || t(messages.toast.smsTemplate.updated));
      navigate(masters.smsTemplate.list);
    } catch (error) {
      console.error("Update SMS template error:", error);
      toast.error(t(messages.toast.smsTemplate.updateFailed));
    } finally {
      setIsLoading(false);
    }
  };

  // Initial values for the SMS template form
  const initialValues = useMemo(() => {
    
    
    
    
    

    
    // Default structure with languages (like add page)
    const defaultValues = {
      id: id,
      slug: "",
      parameter: "",
      sms_templates: languages.map((lang) => ({
        language: lang.name,
        language_id: lang.id,
        title: "",
        content: "",
        uuid: null
      }))
    };
    
    // If SMS template data is loaded, update the default values
    if (smsTemplateData && smsTemplateData.templates) {
      
      
      const updatedValues = {
        ...defaultValues,
        slug: smsTemplateData.slug,
        parameter: smsTemplateData.parameter,
        sms_templates: defaultValues.sms_templates.map((langTemplate) => {
          // Find corresponding template from API data using languageId (not language_id)
          const apiTemplate = smsTemplateData.templates.find(
            template => String(template.languageId) === String(langTemplate.language_id)
          );
          
          if (apiTemplate) {
            
            return {
              ...langTemplate,
              title: apiTemplate.title,
              content: apiTemplate.content,
              uuid: apiTemplate.uuid
            };
          }
          
          return langTemplate;
        })
      };
      
      
      return updatedValues;
    }
    
    
    return defaultValues;
  }, [languages, smsTemplateData, id]);

  // Validation schema for the SMS template form
  const validationSchema = useMemo(
    () => SmsTemplateValidationClass.smsTemplateValidation(),
    []
  );

  if (isDataLoading) {
    return (
      <CCardGroup>
        <CCard className="p-4">
          <div className="text-center">
            <p>Loading SMS template data...</p>
          </div>
        </CCard>
      </CCardGroup>
    );
  }

  return (
    <CCardGroup>
      <CCard className="p-4">
        <h1 className="text-center">{t(smsTemplate.edit.main_heading)}</h1>
        <SmsTemplateForm
          initialValues={initialValues}
          onSubmit={handleUpdateSmsTemplate}
          smsTemplateLoaded={isLoading}
          validationSchema={validationSchema}
        />
      </CCard>
    </CCardGroup>
  );
};

// Static data that would have been passed via getServerSideProps
EditSmsTemplate.breadCrumbArray = [
  {
    name: breadcrumb_key.masters?.smsTemplate?.list,
    pathname: masters.smsTemplate.list,
    active: false,
  },
  {
    name: breadcrumb_key.masters?.smsTemplate?.edit,
    pathname: '',
    active: true,
  },
];

export default EditSmsTemplate;
